// Copyright Materialize, Inc. and contributors. All rights reserved.
//
// Use of this software is governed by the Business Source License
// included in the LICENSE file.
//
// As of the Change Date specified in that file, in accordance with
// the Business Source License, use of this software will be governed
// by the Apache License, Version 2.0.

import { QueryKey } from "@tanstack/react-query";
import { InferResult, sql } from "kysely";

import { executeSqlV2, queryBuilder } from "~/api/materialize";

/**
 * The most recent utilization sample per replica, from the same `_3h` view the
 * cluster detail charts read, so both surfaces show the same number.
 *
 * NOTE: fractions, not percentages. The hour bound leaves a replica with no
 * recent sample blank rather than hours stale.
 */
export function buildReplicaUtilizationQuery() {
  return queryBuilder
    .selectFrom("mz_console_cluster_utilization_overview_3h")
    .distinctOn("replica_id")
    .where(sql<boolean>`mz_now() <= occurred_at + INTERVAL '1 hour'`)
    .select([
      "replica_id as replicaId",
      "cpu_percent as cpuPercent",
      "memory_percent as memoryPercent",
      "disk_percent as diskPercent",
      "heap_percent as heapPercent",
    ])
    .orderBy("replica_id")
    .orderBy("occurred_at", "desc");
}

export type ReplicaUtilization = InferResult<
  ReturnType<typeof buildReplicaUtilizationQuery>
>[0];

/** Fetches the latest utilization sample for every replica in the environment. */
export async function fetchReplicaUtilization({
  queryKey,
  requestOptions,
}: {
  queryKey: QueryKey;
  requestOptions?: RequestInit;
}) {
  const compiledQuery = buildReplicaUtilizationQuery().compile();
  return executeSqlV2({ queries: compiledQuery, queryKey, requestOptions });
}
